import React from "react";
import { ENGINE_LABELS, adoptRowLabel, isEngineApplied, summarySentence } from "../../utils/redesignProvenance";

// The Save tab's engine surface: which engines compose the version, what their
// composition actually put into it, and what it could not carry.
//
// The through-line for every piece of copy here is the gap between what the analyst
// saw on an engine's Redesign tab and what a process version can physically store.
// A version holds structure — tasks, order, gateways. Durations and RACI changes
// have no field, so an engine heuristic that only moves those disappears on save
// unless this panel says otherwise.

const cardStyle = {
  border: "1px solid var(--gray-200)",
  borderRadius: 10,
  background: "var(--color-surface)",
  padding: "12px 14px",
  marginBottom: 14,
};

const labelStyle = {
  fontSize: 12,
  fontWeight: 700,
  color: "var(--gray-500)",
  marginBottom: 8,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

function EngineCheckbox({ engine, checked, disabled, hint, onChange }) {
  return (
    <label
      title={hint}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 7,
        fontSize: 13,
        color: disabled ? "var(--gray-400)" : "var(--gray-700)",
        cursor: disabled ? "default" : "pointer",
      }}
    >
      <input type="checkbox" checked={checked} disabled={disabled} onChange={onChange} />
      <span style={{ fontWeight: 700 }}>{ENGINE_LABELS[engine] || engine}</span>
      {hint && <span style={{ fontSize: 11, color: "var(--gray-400)", fontWeight: 400 }}>{hint}</span>}
    </label>
  );
}

/**
 * The three checkboxes above the review table. The agent is permanently checked and
 * disabled — it is the base this version is built from, not one option among three.
 * RL and BPR enable only once a run for this process exists, because composing one
 * without its reference means the backend re-runs the engine.
 */
export function EngineSelector({ selection }) {
  const { rlAvailable, bprAvailable, selected, toggle, loading, error, subprocessFlagMismatch, includeSubprocessRedesigns } = selection;
  return (
    <div style={cardStyle}>
      <div style={labelStyle}>Engines composing this version</div>
      <div style={{ display: "flex", gap: 22, flexWrap: "wrap", alignItems: "center" }}>
        <EngineCheckbox
          engine="agent"
          checked
          disabled
          hint="always included — the base this redesign is built from"
          onChange={() => {}}
        />
        <EngineCheckbox
          engine="rl"
          checked={selected.rl}
          disabled={!rlAvailable}
          hint={rlAvailable ? "reuses the run you approved" : "run RL on the Redesign tab to enable"}
          onChange={() => toggle("rl")}
        />
        <EngineCheckbox
          engine="bpr"
          checked={selected.bpr}
          disabled={!bprAvailable}
          hint={bprAvailable ? "reuses the result you approved" : "run the model on the Redesign tab to enable"}
          onChange={() => toggle("bpr")}
        />
        {loading && <span style={{ fontSize: 12, color: "var(--gray-500)" }}>Rebuilding the version…</span>}
      </div>

      {(selection.selected.rl || selection.selected.bpr) && (
        <div style={{ fontSize: 11.5, color: "var(--gray-500)", marginTop: 8 }}>
          Sub-process redesigns: <b>{includeSubprocessRedesigns ? "included" : "excluded"}</b> — matched to the
          engine run so an expired reference cannot re-run under a different setting.
        </div>
      )}

      {subprocessFlagMismatch && (
        <div style={{ marginTop: 8, fontSize: 12, color: "var(--amber-ink-800)", background: "var(--amber-tint-50)", border: "1px solid var(--amber-tint-200)", borderRadius: 8, padding: "7px 11px" }}>
          RL and the model were run with different sub-process settings, and one save carries only one.
          RL&apos;s setting is being used. If the model&apos;s stored result has expired, its re-run would
          not match what you reviewed — re-run it with the same setting to be sure.
        </div>
      )}

      {error && (
        <div style={{ marginTop: 8, fontSize: 12, color: "var(--red-ink-800)", background: "var(--red-tint-50)", border: "1px solid var(--red-tint-300)", borderRadius: 8, padding: "7px 11px" }}>
          {error}
        </div>
      )}
    </div>
  );
}

/** Inline, non-blocking notices from redesign_provenance — engine failures included. */
export function ProvenanceNotices({ summary }) {
  if (!summary || !summary.divergences.length) return null;
  return (
    <div style={{ marginBottom: 14, display: "flex", flexDirection: "column", gap: 6 }}>
      {summary.divergences.map((d) => (
        <div
          key={d.key}
          style={{
            fontSize: 12.5,
            color: "var(--amber-ink-800)",
            background: "var(--amber-tint-50)",
            border: "1px solid var(--amber-tint-200)",
            borderLeft: "4px solid #f59e0b",
            borderRadius: 8,
            padding: "8px 12px",
          }}
        >
          <div style={{ fontWeight: 700 }}>⚠ {d.title}</div>
          <div style={{ marginTop: 2, color: "var(--amber-ink-900)" }}>{d.detail}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * The body of the confirm step. Built from structural_coverage and
 * structure_applied, because those two are the only description of what the POST
 * will actually write — everything else on this tab describes what the engine
 * proposed.
 */
export function ConfirmSaveBody({ summary }) {
  if (!summary) return null;
  const { counts, coverage, noStructuralChange, adoptable } = summary;
  const sel = summary.selection || {};
  const declined = sel.declined || [];
  const uncredited = sel.uncredited || [];
  return (
    <div style={{ fontSize: 13, color: "var(--gray-700)", lineHeight: 1.55 }}>
      <p style={{ margin: "0 0 10px", fontWeight: 600 }}>{summarySentence(summary)}</p>

      {/* The last gate is the last chance to notice these two, and neither is
          visible anywhere else in this modal: a declined heuristic has left the
          payload entirely, and an uncredited adoption looks identical to a credited
          one once it is a gateway. */}
      {declined.length > 0 && (
        <p style={{ margin: "0 0 10px", background: "var(--gray-50)", border: "1px solid var(--gray-200)", borderRadius: 8, padding: "8px 12px" }}>
          <b>
            {declined.length} heuristic{declined.length === 1 ? "" : "s"} you unticked{" "}
            {declined.length === 1 ? "is" : "are"} not in this version
          </b>{" "}
          — {declined.map((r) => r.name || r.code).join(", ")}.
        </p>
      )}

      {uncredited.length > 0 && (
        <p style={{ margin: "0 0 10px", background: "var(--amber-tint-50)", border: "1px solid var(--amber-tint-200)", borderRadius: 8, padding: "8px 12px", color: "var(--amber-ink-800)" }}>
          <b>
            {uncredited.length} parallel split{uncredited.length === 1 ? "" : "s"} you adopted could not be
            measured as an improvement
          </b>{" "}
          — {uncredited.map((r) => r.name || r.code).join(", ")}. The gateway is still being written; the
          cycle-time gain is unproven.
        </p>
      )}

      {sel.note && (
        <p style={{ margin: "0 0 10px", fontSize: 12.5, color: "var(--gray-500)" }}>{sel.note}</p>
      )}

      {noStructuralChange && (
        <p style={{ margin: "0 0 10px", background: "var(--sky-tint-50)", border: "1px solid var(--sky-tint-200)", borderRadius: 8, padding: "8px 12px", color: "var(--sky-ink-800)" }}>
          {/* Not a failure. An engine that only re-proposes splits the agent already
              found, or whose proposals don't survive validation, has genuinely
              nothing left to add — saying "ignored" would misdescribe that. */}
          The selected engine{coverage.length > 1 ? "s" : ""} produced no structural change beyond the
          agent&apos;s. Everything they proposed was either already in the agent&apos;s redesign or could not
          be shown to improve it. That is a valid result, not a failure — the version below is still the
          full agent redesign.
        </p>
      )}

      <ul style={{ margin: "0 0 10px", paddingLeft: 18 }}>
        <li>
          <b>{counts.eliminated}</b> task{counts.eliminated === 1 ? "" : "s"} eliminated
          {counts.engineEliminations > 0 && ` (${counts.engineEliminations} from the selected engines)`}
        </li>
        <li>
          <b>{counts.acceptedParallel}</b> parallel split{counts.acceptedParallel === 1 ? "" : "s"} accepted
        </li>
        {counts.nonStructuralTotal > 0 && (
          <li>
            <b>{counts.nonStructuralTotal}</b> heuristic{counts.nonStructuralTotal === 1 ? "" : "s"} change
            durations or RACI assignments — a process version has no field for these, so they are{" "}
            <b>not</b> saved as structure. They are listed under &ldquo;Applied but not persistable&rdquo; on the
            Save tab and can be adopted individually.
          </li>
        )}
      </ul>

      {summary.divergences.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>What differs from the redesign you reviewed</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {summary.divergences.map((d) => (
              <li key={d.key} style={{ marginBottom: 3 }}>
                <b>{d.title}.</b> {d.detail}
              </li>
            ))}
          </ul>
        </div>
      )}

      {adoptable.length > 0 && (
        <p style={{ margin: 0, fontSize: 12.5, color: "var(--gray-500)" }}>
          {adoptable.length} heuristic{adoptable.length === 1 ? "" : "s"} in the adopt list will be recorded on
          the redesign scenario only if you adopt them — saving does not adopt them for you.
        </p>
      )}
    </div>
  );
}

/**
 * The adopt list. Every row here is a heuristic with no structural field to land in,
 * so the version cannot carry it; adopting records it against the saved redesign
 * scenario instead.
 *
 * The two statuses render differently on purpose. `applied_not_persistable` means an
 * engine really did this and only the storage can't hold it; `recommended` means
 * nobody applied it and the data merely suggests it. Rendering them the same would
 * either credit the agent with RL's work or demote RL's work to a guess.
 *
 * The sharpest case this covers: RL's `empower` strips the Accountable row and
 * `numerical_involvement` prunes participants — both act on the RACI Matrix tab. But
 * workflow[].job_id carries one job per item, not a RACI set, so a saved version
 * cannot reflect either. Without this list that loss would be silent.
 */
export function AdoptList({ summary, adoptedHeuristics, onToggleAdopt, suppressAppliedSection }) {
  const rows = summary?.adoptable || [];
  if (!rows.length) return null;

  // `adoptable_heuristics` is a subset of `applied_heuristics`, so once the save
  // checklist is on screen its applied_not_persistable rows are already there, each
  // with this same toggle on it. Repeating them here would put one decision in two
  // lists on one tab — and the analyst has no way to tell they are the same one.
  // The suggested rows are NOT duplicated: nobody applied those, so they never
  // appear in applied_heuristics and this stays their only home.
  const applied = suppressAppliedSection ? [] : rows.filter(isEngineApplied);
  const recommended = rows.filter((r) => !isEngineApplied(r));
  if (!applied.length && !recommended.length) return null;

  const renderRow = (row, i) => {
    const adopted = !!(row.code && adoptedHeuristics?.[row.code]);
    const engineApplied = isEngineApplied(row);
    return (
      <div
        key={row.code || i}
        style={{
          background: engineApplied ? "var(--violet-tint-50)" : "#fffdf6",
          border: `1px solid ${engineApplied ? "var(--violet-tint-200)" : "#f0e7cf"}`,
          borderLeft: `4px solid ${engineApplied ? "#7c3aed" : "#d9c58a"}`,
          borderRadius: 8,
          padding: "8px 12px",
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, fontWeight: 700 }}>{row.name}</span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              borderRadius: 999,
              padding: "1px 8px",
              color: engineApplied ? "var(--violet-ink-800)" : "#8a6d1f",
              background: engineApplied ? "var(--violet-tint-100)" : "#fdf3d8",
              border: `1px solid ${engineApplied ? "var(--violet-tint-300)" : "#ecd9a4"}`,
            }}
          >
            {adoptRowLabel(row)}
          </span>
          {row.task_ids?.length > 0 && (
            <span style={{ fontSize: 11, color: "var(--gray-500)" }}>
              {row.task_ids.length} task{row.task_ids.length === 1 ? "" : "s"}
            </span>
          )}
          {/* The adopted set is keyed by code, so a row without one cannot be
              adopted without colliding with every other codeless row. */}
          {!row.code ? null : !adopted ? (
            <button
              type="button"
              className="primary-btn sm"
              style={{ marginLeft: "auto", padding: "1px 12px", fontSize: 11 }}
              onClick={() => onToggleAdopt(row)}
            >
              Apply heuristic
            </button>
          ) : (
            <>
              <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--teal-ink-deep)", background: "var(--teal-tint-100)", borderRadius: 999, padding: "1px 8px" }}>
                ✓ adopted by you — recorded with the saved version
              </span>
              <button
                type="button"
                className="secondary-btn sm"
                style={{ padding: "1px 10px", fontSize: 11 }}
                onClick={() => onToggleAdopt(row)}
              >
                Undo
              </button>
            </>
          )}
        </div>
        {row.basis && <div style={{ fontSize: 11.5, color: "var(--amber-ink-700)", marginTop: 3 }}>because {row.basis}</div>}
        {row.rationale && <div style={{ fontSize: 12, color: "var(--gray-500)", marginTop: 3 }}>{row.rationale}</div>}
        {row.not_persistable_reason && (
          <div style={{ fontSize: 11.5, color: "var(--gray-600)", marginTop: 3, fontStyle: "italic" }}>
            {row.not_persistable_reason}
          </div>
        )}
        {row.impact_note && (
          <div style={{ fontSize: 11.5, color: "var(--slate-600)", marginTop: 4, display: "flex", alignItems: "baseline", gap: 6 }}>
            {/* impact_computable is false for every row here, so adopting one leaves
                the quadrangle numbers flat. That's correct and already precedented —
                it is exactly how control-addition behaves on the current card — but
                only if the note saying so sits next to the button. */}
            <span style={{ fontSize: 10, fontWeight: 700, color: "var(--slate-600)", background: "var(--slate-100)", border: "1px solid var(--slate-200)", borderRadius: 999, padding: "1px 7px", whiteSpace: "nowrap" }}>
              no immediate number change
            </span>
            <span>{row.impact_note}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ marginBottom: 16 }}>
      {applied.length > 0 && (
        <>
          <div style={labelStyle}>
            Applied by the engine but not persistable — the engine made these changes; a process version has no
            field to store them. Adopt one to record it on the saved redesign scenario.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
            {applied.map(renderRow)}
          </div>
        </>
      )}
      {recommended.length > 0 && (
        <>
          <div style={labelStyle}>
            Suggested — nobody applied these; the recorded data indicates them. Adopt to record with the version.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{recommended.map(renderRow)}</div>
        </>
      )}
    </div>
  );
}

/**
 * IDEA 1 — the engine's own end-to-end claim, as a labelled band beneath the KPI
 * tiles rather than inside them.
 *
 * The tiles show what the save WRITES. An engine's full redesign is a different
 * quantity — bigger, and mostly not persistable — so it gets its own row with the
 * "Engine-reported" treatment already used for engine-native figures in the
 * Comparison Report. It answers "what would I get if this were actually done?", a
 * question the tiles cannot answer and currently leave the analyst to reconstruct
 * from a list of per-heuristic rows.
 *
 * It never touches the tile numbers, and it never adds anything up — see
 * readEngineNativeKpis for why summing those per-row deltas is invalid.
 */
export function EnginePotentialBand({ natives, money, fmtMin }) {
  const rows = (natives || []).filter(Boolean);
  if (!rows.length) return null;

  const pct = (m) => {
    if (!m || m.reduction_pct == null) return null;
    const v = Number(m.reduction_pct);
    const better = v > 0;
    return (
      <span style={{ fontWeight: 800, color: better ? "var(--emerald-ink-600)" : v < 0 ? "var(--red-ink-600)" : "var(--gray-500)", marginLeft: 6 }}>
        {better ? "▼" : v < 0 ? "▲" : "–"} {Math.abs(v).toFixed(1)}%
      </span>
    );
  };

  return (
    <div style={{ border: "1px solid var(--amber-tint-200)", background: "var(--amber-tint-50)", borderRadius: 10, padding: "10px 14px", marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: "var(--yellow-ink-800)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
          Engine-reported potential
        </span>
        <span style={{ fontSize: 11.5, color: "var(--amber-ink-800)" }}>
          each engine&apos;s own figure for its <b>full</b> redesign — not what this save writes
        </span>
      </div>

      {rows.map((n) => (
        <div key={n.engine} style={{ display: "flex", gap: 22, flexWrap: "wrap", alignItems: "baseline", padding: "4px 0" }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: "var(--yellow-ink-800)", minWidth: 34 }}>{n.label}</span>
          {n.time && (
            <span style={{ fontSize: 13, color: "var(--gray-700)", fontVariantNumeric: "tabular-nums" }}>
              <span style={{ color: "var(--gray-500)", fontSize: 11 }}>Cycle time </span>
              {fmtMin(n.time.as_is)} → <b>{fmtMin(n.time.to_be)}</b>
              {pct(n.time)}
            </span>
          )}
          {n.cost && (
            <span style={{ fontSize: 13, color: "var(--gray-700)", fontVariantNumeric: "tabular-nums" }}>
              <span style={{ color: "var(--gray-500)", fontSize: 11 }}>{n.costLabel} </span>
              {money(n.cost.as_is)} → <b>{money(n.cost.to_be)}</b>
              {pct(n.cost)}
              {/* RL's cost is Responsible-role-only; the tile above is whole-process.
                  Same word, different quantity — so the scope is stated, not implied. */}
              {n.costScopeNote && (
                <span title={n.costScopeNote} style={{ marginLeft: 4, color: "var(--amber-ink-700)", fontWeight: 700, cursor: "help" }}>ⓘ</span>
              )}
            </span>
          )}
        </div>
      ))}

      <div style={{ fontSize: 11, color: "var(--amber-ink-800)", marginTop: 6 }}>
        Reaching these figures means implementing the changes in the process data and re-running the
        analysis. They are the engine&apos;s own measurement on its own scale, so they are not
        interchangeable with the tiles above and the two must never be added together.
      </div>
    </div>
  );
}

/**
 * IDEA 2 — say why the tiles are not moving.
 *
 * Without this, adopting three heuristics and watching the quadrangle sit at
 * 1288.15 → 1288.15 reads as a broken button. It is not: these heuristics change
 * things the CMS records no field for, so there is nothing to recompute from. The
 * adoption did do something real — the heuristic is recorded on the saved redesign
 * scenario — and that is worth stating in the same breath as the reason.
 */
export function KpiExclusionNote({ summary, adoptedHeuristics }) {
  const rows = summary?.adoptable || [];
  if (!rows.length) return null;
  const adoptedCount = rows.filter((r) => r.code && adoptedHeuristics?.[r.code]).length;

  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12.5, color: "var(--slate-700)", background: "var(--slate-50)", border: "1px solid var(--slate-200)", borderRadius: 8, padding: "8px 12px", marginBottom: 16 }}>
      <span aria-hidden="true" style={{ fontWeight: 800, color: "var(--slate-500)" }}>ⓘ</span>
      <span>
        {adoptedCount > 0 ? (
          <>
            <b>{adoptedCount} adopted heuristic{adoptedCount === 1 ? " is" : "s are"} not included in the figures above.</b>{" "}
          </>
        ) : (
          <>
            <b>{rows.length} heuristic{rows.length === 1 ? "" : "s"} below {rows.length === 1 ? "is" : "are"} not included in the figures above.</b>{" "}
          </>
        )}
        They change things the CMS records no field for — a merged task&apos;s duration, a trust
        arrangement, a capacity increase — so there is no stored number to recompute them from.
        {adoptedCount > 0 && (
          <> Adopting them is still recorded: {adoptedCount === 1 ? "it appears" : "they appear"} on the saved
          redesign scenario with your name against {adoptedCount === 1 ? "it" : "them"}.</>
        )}{" "}
        These figures will move once the change is made in the process data and the analysis is re-run.
      </span>
    </div>
  );
}
